import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useHistory } from "react-router-dom";
import ACLProfile from '@app/api/aclprofile/aclprofile.actions';
import Site from '@app/api/site/site.actions';
import Wan from '@app/api/wan/wan.actions';
import { Spinner } from '@patternfly/react-core';
import { PatternflyTable } from '@app/components/PatternflyTable';
import { sortable, cellWidth } from '@patternfly/react-table';
import { truncateString } from '@app/utils/utils';
import { sortBy } from '@app/lib/functions';
import { PageSection, Title, Modal, ModalVariant } from "@patternfly/react-core";
import { BullhornIcon, EllipsisVIcon } from '@patternfly/react-icons';
import '../../app.css';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import { Card, CardBody, Col, Button, Row, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { Column, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, Updater, useReactTable } from '@tanstack/react-table';
import { MdArrowDownward, MdArrowUpward, MdCircle, MdUnfoldMore } from 'react-icons/md';
import Loader from '@app/components/Loader';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { actionCreators as sessionActionCreators } from '@app/common/session/session.reducer';
import UserSession from '@app/common/user-session';

type ColumnFiltersState = ColumnFilter[];
interface ColumnFilter {
  id: string;
  value: unknown;
}

interface ThemeContextType {
  theme: string;
  setTheme: (theme: string) => void;
}

const ACLActionsMenu = ({ acl, basePath, isManager, onDelete }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)} direction="start">
      <DropdownToggle
        color="link"
        className="list-actions-toggle"
        aria-label={`Actions for ${acl.name}`}
      >
        <EllipsisVIcon />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem tag={Link} to={`${basePath}/aclProfile/edit/${acl.id}`}>Edit</DropdownItem>
        <DropdownItem divider />
        <DropdownItem
          className="list-action-danger"
          disabled={!isManager}
          onClick={() => onDelete(acl.id, acl.name)}
        >
          Delete
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const ACLProfileList: React.FunctionComponent = ({}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);
  const [aclProfiles, setAclProfiles] = React.useState([]);
  const [aclProfilesData, setAclProfilesData] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const { theme } = React.useContext(ThemeContext) as ThemeContextType;
  const [expandedRows, setExpandedRows] = React.useState({});

  const basePath = process.env.REACT_APP_BASE_PATH || '';
  const isManager = UserSession.getParam('role')?.toLowerCase() === 'manager';

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )

  const toggleRow = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const statuses = [
    "#85ba3b",
    "#e93a54",
    "#000"
  ];

  const generateRandomNumber = () => {
    const randomNumber = Math.floor(Math.random() * statuses.length);
    return statuses[randomNumber];
  }

  React.useEffect(() => {
    (async function() {
      try {
        const acls = await ACLProfile.getAll();
        const sites = await Site.getAll();
        const wans = await Wan.getAll();
        const sorted = sortBy(acls, 'name');
        let aclProfileRows = [];
        let aclProfileDataRows = [];

        if (sorted) {
          aclProfileRows = sorted.map(item => {
            return aclProfileFromItem(sorted, item, sites);
          });
          aclProfileDataRows = sorted.map(item => {
            return aclProfileDataFromItem(sorted, item, sites, wans);
          });
        }

        setAclProfiles(aclProfileRows);
        setAclProfilesData(aclProfileDataRows);
        setLoading(false);
      }
      catch (error) {
        console.log(error);
        setAclProfiles([]);
        setAclProfilesData([]);
        setLoading(false);
      }
    })();
  }, []);

  const columns = React.useMemo(() => {
    const unorderedColumns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <>
          <strong>
            <Link to={`${basePath}/aclprofile/${row.original.id}`} className="acl-name">
              {row.original.name}
            </Link>
          </strong>
        </>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'sitesCount',
      header: 'Assignments',
      enableColumnFilter: false,
    },
    {
      accessorKey: 'id',
      header: 'ID',
    },
    {
      id: 'actions',
      header: '',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <ACLActionsMenu
          acl={row.original}
          basePath={basePath}
          isManager={isManager}
          onDelete={deleteACLProfile}
        />
      ),
    },
    ];

    const columnOrder = ['name', 'id', 'description', 'sitesCount', 'actions'];
    return columnOrder.map((columnId) => unorderedColumns.find((column) => (column.id || column.accessorKey) === columnId)!);
  }, [basePath, isManager]);

  const table = useReactTable({
    data: aclProfilesData,
    columns,
    filterFns: {},
    state: {
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
  });

  const getColumns = () => {
    return [
      { title: 'Name', transforms: [sortable] },
      { title: 'ID', props: { className: 'pf-m-hidden pf-m-hidden-on-md' }},
      { title: 'Description' },
      { title: 'Assignments', transforms: [sortable] },
      { title: '', transforms: [cellWidth(10)] }
    ];
  }

  const getColumnIndex = (columnName) => {
    const columns = getColumns();
    for (var i = 0; i < columns.length; i++) {
      if (columns[i]['title'] === columnName) {
        return i;
      }
    }
    return -1;
  }

  const editACLProfile = (event, rowId, rowData) => {
    const aclProfileId = rowData['id']['title'];
    history.push("/aclProfile/edit/" + aclProfileId);
  }

  const deleteACLProfile = async (aclProfileId, aclProfileName) => {
    Swal.fire({
      title: 'Confirm Action',
      html: `<div style="text-align: left;">
               <p>Are you sure you want to delete this ACL Profile?</p>
               <strong>Name:</strong> ${aclProfileName}
             </div>`,
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteRow(aclProfileId);
      }
    });
  };

  const deleteRow = async (aclProfileId) => {
    try {
      const aclProfile = await ACLProfile.get(aclProfileId);

      if (aclProfile.assignments?.length > 0) {
        Swal.fire({
          title: 'Error',
          text: 'This ACL Profile has Sites or pWANs assigned. You must unassign this from all Sites and pWANs before it can be deleted.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      const result = await ACLProfile.delete(aclProfileId);

      if (result?.data?.request_id) {
        
        try {
          const acls = await ACLProfile.getAll();
          const sites = await Site.getAll();
          const wans = await Wan.getAll();
          const sorted = sortBy(acls, 'name');
          let aclProfileRows = [];
          let aclProfileDataRows = [];

          if (sorted) {
            aclProfileRows = sorted.map(item => {
              return aclProfileFromItem(sorted, item, sites);
            });
            aclProfileDataRows = sorted.map(item => {
              return aclProfileDataFromItem(sorted, item, sites, wans);
            });
          }

          setAclProfiles(aclProfileRows);
          setAclProfilesData(aclProfileDataRows);
          setPage(1);
          
          // Show success notification
          dispatch(sessionActionCreators.notificationEnqueue({
            message: 'ACL Profile Deleted',
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'success',
            },
          }));
        } catch (refreshError) {
          

          const aclProfileIdIndex = getColumnIndex('ID');
          const updatedProfiles = aclProfiles.filter(a => a.cells[aclProfileIdIndex].title !== aclProfileId);
          const updatedProfilesData = aclProfilesData.filter(a => a.id !== aclProfileId);

          setAclProfiles(updatedProfiles);
          setAclProfilesData(updatedProfilesData);
          setPage(1);
          
          dispatch(sessionActionCreators.notificationEnqueue({
            message: 'ACL Profile Deleted',
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'success',
            },
          }));
        }
      }
      else {
        Swal.fire({
          title: 'Error',
          text: 'There was an error deleting the ACL Profile, or you do not have the permissions to perform this action',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'There was an error deleting the ACL Profile',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }



  const aclProfileFromItem = (acls, item, allSites) => {
    let sitesCount = 0;

    acls.map(acl => {
      if (acl.id === item.id && acl.assignments) {
        let sites = acl.assignments.filter(a => a.resource_type === 'site');
        let wans = acl.assignments.filter(a => a.resource_type === 'wan');

        if (sites?.length > 0) {
          sitesCount += sites.length;
        }
        if (wans?.length > 0) {
          wans.map(wan => {
            const wanSites = allSites.filter(site => site.wanId === wan.resource_id);
            const siteCount = wanSites ? wanSites.length : 0;
            sitesCount += siteCount;
          });
        }
      }
    });

    return {
      cells: [
        { title: <Link to={{ pathname: `${basePath}/aclprofile/` + item.id }}>{item.name}</Link> },
        { title: truncateString(item.description, 50) },
        { title: sitesCount },
        { title: item.id, props: { className: 'pf-m-hidden pf-m-hidden-on-md' }}
      ]
    }
  }

  const aclProfileDataFromItem = (acls, item, allSites, allWans) => {
    let sitesCount = 0;
    let sitesData = [];
    let wansData = [];

    acls.map(acl => {
      if (acl.id === item.id && acl.assignments) {
        let sites = acl.assignments.filter(a => a.resource_type === 'site');
        let wans = acl.assignments.filter(a => a.resource_type === 'wan');

        if (sites?.length > 0) {
          sitesCount += sites.length;
          sitesData = sites.map(siteAssignment => {
            const siteObj = allSites.find(s => s.id === siteAssignment.resource_id);
            return {
              id: siteAssignment.resource_id,
              name: siteObj ? (siteObj.Name || siteObj.name || siteObj.address1) : '',
              type: 'Site'
            };
          });
        }
        if (wans?.length > 0) {
          wansData = wans.map(wanAssignment => {
            const wanObj = allWans.find(w => w.id === wanAssignment.resource_id);
            return {
              id: wanAssignment.resource_id,
              name: wanObj ? wanObj.name : '',
              type: 'pWAN'
            };
          });
          sitesCount += wans.length;
        }
      }
    });

    // Combine sites and wans for the expandable list
    const combined = [...sitesData, ...wansData];

    return {
      name: item.name,
      description: truncateString(item.description, 50),
      sitesCount: sitesCount,
      sites: combined,
      id: item.id
    }
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>ACL Profiles</title>
      </Helmet>
      {loading ? (
        <Loader />
      ) : (
        <Page
          tag="div"
          className={`cr-page px-3 pt-2 acl-list h-100 ${theme}`}
          title=""
          breadcrumbs={[{ name: 'ACL Profiles', active: true }]}
          actionButton={
            <Link to={`${basePath}/aclprofile/new`}>
              <Button className="ethica-button-green" size="sm">Add ACL</Button>
            </Link>
          }
        >
          <>
            <Row>
              <Col lg={12} md={12} sm={12} xs={12} className="mb-3">
                <div className="sites-card-wrapper">
                  <Card className="p-3">
                    {loading ? (
                        <Spinner size="lg" />
                    ) : aclProfiles.length === 0 ? (
                        <>
                        <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>ID</th>
                                <th className="text-start">Description</th>
                                <th className="text-start">Assignments</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td colSpan={5} className="text-center">No data available</td>
                              </tr>
                            </tbody>
                          </table>
                        </>
                      ) : (
                        <>
                        <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                  {headerGroup.headers.map(header => (
                                    <th key={header.id} colSpan={header.colSpan}>
                                      {header.isPlaceholder ? null : (
                                        <div className={`d-flex align-items-center ${header.column.columnDef.header === 'Description' || header.column.columnDef.header === 'Assignments' ? 'justify-content-start' : (header.column.getCanFilter() ? 'justify-content-start' : 'justify-content-center')}`}>
                                          <div
                                            {...{
                                              className: header.column.getCanSort()
                                                ? 'select-none table-column-header'
                                                : 'table-column-header',
                                              onClick: header.column.getToggleSortingHandler(),
                                            }}
                                          >
                                            {flexRender(
                                              header.column.columnDef.header,
                                              header.getContext()
                                            )}

                                            {header.column.getCanSort() && (
                                              {
                                                asc: <MdArrowUpward className="ms-2" />,
                                                desc: <MdArrowDownward className="ms-2" />,
                                              }[header.column.getIsSorted() as string] ?? (
                                                <MdUnfoldMore className="ms-2 opacity-50" />
                                              )
                                            )}
                                          </div>
                                          {header.column.getCanFilter() ? (
                                            <div className="d-flex justify-content-end">
                                              <Filter column={header.column} />
                                            </div>
                                          ) : null}
                                        </div>
                                      )}
                                    </th>
                                  ))}
                                </tr>
                              ))}
                            </thead>
                            <tbody>
                              {table.getRowModel().rows.map(row => (
                                (() => {
                                  
                                  return (
                                    <React.Fragment key={row.id}>
                                      <tr>
                                        {row.getVisibleCells().map(cell => (
                                        <td
                                          key={cell.id}
                                          className={cell.column.columnDef.header === 'Description' || cell.column.columnDef.header === 'Assignments' ? 'text-start' : (cell.column.id === 'actions' ? 'text-center' : (cell.column.getCanFilter() ? '' : 'text-center'))}
                                          style={{
                                            overflow: cell.column.id === 'actions' ? 'visible' : 'hidden',
                                            position: cell.column.id === 'actions' ? 'relative' : undefined,
                                            zIndex: cell.column.id === 'actions' ? 2 : undefined,
                                          }}
                                        >
                                            {row.original.sites.length > 0 && cell.column.columnDef.header === 'Name' ? (
                                              <>
                                                <button onClick={() => toggleRow(row.id)} className="table-arrow">
                                                  {expandedRows[row.id] ? '▼' : '▶'}
                                                </button>
                                                <strong>{flexRender(cell.column.columnDef.cell, cell.getContext())}</strong>
                                              </>
                                            ) : (
                                            <span className={`no-arrow d-flex flex-wrap-nowrap w-100 ${cell.column.columnDef.header === 'Description' || cell.column.columnDef.header === 'Assignments' ? 'justify-content-start' : (cell.column.id === 'actions' ? 'justify-content-center' : '')}`}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                                            )}
                                          </td>
                                        ))}
                                      </tr>
                                      {expandedRows[row.id] && row.original.sites && row.original.sites.length > 0 && (
                                        <tr>
                                          <td colSpan={5} style={{ padding: '0', borderTop: '1px solid #dee2e6' }}>
                                            <div style={{ margin: '0', padding: '10px 0', borderBottom: 'none', width: '100%' }}>
                                              <div style={{ 
                                                display: 'flex',
                                                width: '100%',
                                                paddingLeft: '34px',
                                                borderBottom: '1px solid #dee2e6',
                                                backgroundColor: '#f8f9fa'
                                              }}>
                                                <div style={{ 
                                                  width: '20%',
                                                  padding: '8px',
                                                  fontSize: '12px',
                                                  color: '#333',
                                                  fontWeight: '600',
                                                  textAlign: 'left'
                                                }}>
                                                  Name
                                                </div>
                                                <div style={{ 
                                                  width: '35%',
                                                  padding: '8px',
                                                  fontSize: '12px',
                                                  color: '#333',
                                                  fontWeight: '600',
                                                  textAlign: 'left'
                                                }}>
                                                  ID
                                                </div>
                                                <div style={{ 
                                                  width: '10%',
                                                  padding: '8px',
                                                  fontSize: '12px',
                                                  color: '#333',
                                                  fontWeight: '600',
                                                  textAlign: 'left'
                                                }}>
                                                  Type
                                                </div>
                                              </div>
                                              {row.original.sites
                                                .filter(resource => resource.name !== 'Unknown Site' && resource.name !== 'Unknown pWAN')
                                                .map((resource, index) => (
                                                  <div 
                                                    key={index} 
                                                    className="expanded-gateway-row"
                                                    style={{ 
                                                      display: 'flex',
                                                      width: '100%',
                                                      paddingLeft: '34px',
                                                      borderTop: index === 0 ? 'none' : '1px solid #dee2e6'
                                                    }}
                                                  >
                                                    <div style={{ 
                                                      width: '20%',
                                                      padding: '8px',
                                                      fontSize: '12px',
                                                      color: '#222222',
                                                      textAlign: 'left'
                                                    }}>
                                                      {resource.name}
                                                    </div>
                                                    <div style={{ 
                                                      width: '35%',
                                                      padding: '8px',
                                                      fontSize: '12px',
                                                      color: '#222222',
                                                      textAlign: 'left'
                                                    }}>
                                                      {resource.id}
                                                    </div>
                                                    <div style={{ 
                                                      width: '10%',
                                                      padding: '8px',
                                                      fontSize: '12px',
                                                      color: '#222222',
                                                      textAlign: 'left'
                                                    }}>
                                                      {resource.type}
                                                    </div>
                                                  </div>
                                                ))
                                              }
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })()
                              ))}
                            </tbody>
                          </table>

                          {/* Пагинация */}
                          <div className="d-flex items-center justify-content-center align-items-center flex-wrap mt-2">
                            <div className="me-2">
                              <span className="flex items-center gap-1">
                                Go to page:
                                <input
                                  type="number"
                                  min="1"
                                  max={table.getPageCount()}
                                  defaultValue={table.getState().pagination.pageIndex + 1}
                                  onChange={e => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0
                                    table.setPageIndex(page)
                                  }}
                                  className="border p-1 rounded w-16"
                                />
                              </span>
                            </div>
                            <div>
                                <button
                                  className="border rounded p-1 me-1"
                                  onClick={() => table.setPageIndex(0)}
                                  disabled={!table.getCanPreviousPage()}
                                >
                                  {'<<'}
                                </button>
                                <button
                                  className="border rounded p-1 me-1"
                                  onClick={() => table.previousPage()}
                                  disabled={!table.getCanPreviousPage()}
                                >
                                  {'<'}
                                </button>
                                <span className="flex items-center me-1">
                                  <strong>
                                    {table.getState().pagination.pageIndex + 1} of{' '}
                                    {table.getPageCount()}
                                  </strong>
                                </span>
                                <button
                                  className="border rounded p-1 me-1"
                                  onClick={() => table.nextPage()}
                                  disabled={!table.getCanNextPage()}
                                >
                                  {'>'}
                                </button>
                                <button
                                  className="border rounded p-1 me-2"
                                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                  disabled={!table.getCanNextPage()}
                                >
                                  {'>>'}
                                </button>
                              </div>
                              <div>
                                <select
                                  className="form-select"
                                  value={table.getState().pagination.pageSize}
                                  onChange={e => {
                                    table.setPageSize(Number(e.target.value))
                                  }}
                                >
                                  {[10, 20, 30, 40, 50].map(pageSize => (
                                    <option key={pageSize} value={pageSize}>
                                      Show {pageSize}
                                    </option>
                                  ))}
                                </select>
                              </div>
                          </div>
                        </>
                      )}
                  </Card>
                </div>
              </Col>
            </Row>
          </>


        </Page>
      )}
    </React.Fragment>
  );
}

function setColumnFilters(updaterOrValue: Updater<ColumnFiltersState>): void {
  throw new Error('Function not implemented.');
}

function Filter({ column }: { column: Column<any, unknown> }) {
  const columnFilterValue = column.getFilterValue();
  //@ts-ignore
  const { filterVariant } = column.columnDef.meta ?? {}

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <select
      onChange={e => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      {/* See faceted column filters example for dynamic select options */}
      <option value="">All</option>
      <option value="complicated">complicated</option>
      <option value="relationship">relationship</option>
      <option value="single">single</option>
    </select>
  ) : (
    <DebouncedInput
      className="form-control"
      onChange={value => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
    // See faceted column filters example for datalist search suggestions
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  )
}
